import type { CSSObject } from "type/tss";
import { Theme } from "@mui/material/styles";
import * as env from "shared/env";

export const styles = (theme: Theme) => {
	return {
		dialogPaper: {
			borderRadius: 4,
			[theme.breakpoints.down("xs")]: {
				margin: 0,
				maxHeight: "100%",
				height: "100%",
				maxWidth: "100%",
				width: "100%",
				borderRadius: 0,
			},
		},
		dialogTitle: {
			backgroundColor: theme.palette.custom.dialogBackground,
			color: theme.palette.common.white,
			display: "flex",
			alignItems: "center",
			justifyContent: "space-between",
			padding: theme.spacing(2),
			fontFamily: theme.typography.fontFamily,
			fontSize: 24,
			fontWeight: 400,
			lineHeight: "1.2",
		},
		closeButton: {
			color: theme.palette.common.white,
			padding: 0,
			"&:hover": {
				backgroundColor: "rgba(255, 255, 255, 0.1)",
			},
		},
		dialogContentRoot: {
			padding: theme.spacing(3, 2),
			backgroundColor: theme.palette.background.paper,
			[theme.breakpoints.up("sm")]: {
				padding: theme.spacing(4, 3),
			},
		},
		sectionsContainer: {
			display: "flex",
			flexDirection: "column",
			gap: theme.spacing(4),
		},
		otherSection: {
			display: "flex",
			flexDirection: "column",
			gap: theme.spacing(2),
		},
		sectionTitle: {
			fontFamily: theme.typography.fontFamily,
			fontSize: 16,
			fontWeight: 700,
			lineHeight: "20px",
			color: theme.palette.custom.textPrimary,
			marginBottom: theme.spacing(2),
		},
		checkboxGroup: {
			display: "flex",
			flexDirection: "column",
			gap: theme.spacing(3),
		},
		checkboxItem: {
			display: "flex",
			alignItems: "flex-start",
			gap: theme.spacing(1),
		},
		checkbox: {
			padding: 0,
			width: 24,
			height: 24,
			borderRadius: 4,
			flexShrink: 0,
			"&.MuiCheckbox-root": {
				color: theme.palette.grey[400],
			},
			"&.Mui-checked": {
				color: theme.palette.custom.primary,
				backgroundColor: "transparent",
			},
			"& .MuiSvgIcon-root": {
				fontSize: 24,
			},
		},
		checkboxLabel: {
			fontFamily: theme.typography.fontFamily,
			fontSize: 16,
			fontWeight: 400,
			lineHeight: "24px",
			color: theme.palette.custom.textPrimary,
			margin: 0,
		},
		selectField: {
			"& .MuiOutlinedInput-root": {
				borderRadius: 8,
				backgroundColor: theme.palette.background.paper,
				minHeight: 52,
				fontFamily: theme.typography.fontFamily,
				fontSize: 16,
			},
			"& .MuiOutlinedInput-notchedOutline": {
				borderColor: theme.palette.custom.border,
				borderWidth: 1,
			},
			"& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
				borderColor: theme.palette.custom.border,
			},
			"& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
				borderColor: theme.palette.custom.primary,
				borderWidth: 1,
			},
			"& .MuiInputLabel-outlined": {
				fontFamily: theme.typography.fontFamily,
				fontSize: 14,
				color: theme.palette.custom.textSecondary,
				backgroundColor: theme.palette.background.paper,
				padding: theme.spacing(0, 0.5),
			},
			"& .MuiInputLabel-outlined.MuiInputLabel-shrink": {
				transform: "translate(14px, -6px) scale(1)",
				fontSize: 14,
			},
			"& .MuiSelect-select": {
				fontFamily: theme.typography.fontFamily,
				fontSize: 16,
				color: theme.palette.custom.textPrimary,
			},
			"& .MuiSelect-icon": {
				color: theme.palette.custom.textSecondary,
			},
		},
		toggleContainer: {
			display: "flex",
			alignItems: "center",
			gap: theme.spacing(2),
		},
		toggleSwitch: {
			width: 48,
			height: 24,
			padding: 0,
			"& .MuiSwitch-switchBase": {
				padding: 4,
				"&.Mui-checked": {
					transform: "translateX(24px)",
					"& + .MuiSwitch-track": {
						backgroundColor: theme.palette.custom.primary,
						opacity: 1,
					},
				},
			},
			"& .MuiSwitch-thumb": {
				width: 16,
				height: 16,
				backgroundColor: theme.palette.common.white,
			},
			"& .MuiSwitch-track": {
				borderRadius: 12,
				backgroundColor: theme.palette.grey[400],
				opacity: 1,
			},
		},
		toggleLabel: {
			fontFamily: theme.typography.fontFamily,
			fontSize: 16,
			fontWeight: 400,
			lineHeight: "20px",
			color: theme.palette.custom.textPrimary,
			flex: 1,
		},
		dialogActions: {
			padding: theme.spacing(2),
			gap: theme.spacing(1),
			justifyContent: "flex-end",
			backgroundColor: theme.palette.background.paper,
			borderTop: "none",
			[theme.breakpoints.down("xs")]: {
				flexDirection: "row",
			},
		},
		clearButton: {
			borderRadius: 4,
			padding: theme.spacing(1.25),
			boxShadow: "0px 1px 2px 0px rgba(0,0,0,0.25)",
			fontSize: 16,
			lineHeight: 1.2,
			fontFamily: theme.typography.fontFamily,
			fontWeight: 600,
			textTransform: "none" as const,
		},
		viewButton: {
			borderRadius: 4,
			padding: theme.spacing(1.25),
			boxShadow: "0px 1px 2px 0px rgba(0,0,0,0.25)",
			fontSize: 16,
			lineHeight: 1.2,
			fontFamily: theme.typography.fontFamily,
			fontWeight: 600,
			textTransform: "none" as const,
		},
		header: {
			paddingBottom: 20,
		},
		chipContainer: {
			marginTop: 10,
		},
		chip: {
			margin: theme.spacing(0.5),
			fontSize: "1rem",
		},
		filters: {
			height: "100%",
		},
	} satisfies Record<string, CSSObject>;
};
